import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { isArray, isNil } from 'lodash';
import * as classnames from 'classnames';

class Square extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isHover: false,
      isPreview: false,
      previewX: 0,
      previewY: 0
    };

    /**
     * This variable is useful because the dragEnter & dragExit events get confused when hovering children nodes inside the square.
     * @type {null}
     */
    this.enterTarget = null;

    this.onDropHandler = this.onDropHandler.bind(this);
    this.onDragEnterHandler = this.onDragEnterHandler.bind(this);
    this.onDragLeaveHandler = this.onDragLeaveHandler.bind(this);
    this.onMouseMoveHandler = this.onMouseMoveHandler.bind(this);
    this.onMouseEnterHandler = this.onMouseEnterHandler.bind(this);
    this.onMouseLeaveHandler = this.onMouseLeaveHandler.bind(this);
  }

  /**
   * Handler when a dragging event enters this component.
   * @param e
   */
  onDragEnterHandler(e) {
    if (this.props.animateOnHover) {
      e.preventDefault();
      e.stopPropagation();
      this.enterTarget = e.target;
      if (this.props.occurrence === null) {
        this.setState({
          isHover: true
        });
      } else {
        const type = isArray(this.props.occurrence)
          ? this.props.occurrence[0].type
          : this.props.occurrence.type;
        if (e.dataTransfer.types.indexOf(`type/${type}`) > -1) {
          this.setState({
            isHover: true
          });
        }
      }
    }
  }

  /**
   * Handler when a dragging event exits this component.
   * @param e
   */
  onDragLeaveHandler(e) {
    if (this.props.animateOnHover && this.enterTarget === e.target) {
      e.preventDefault();
      e.stopPropagation();
      this.setState({
        isHover: false
      });
    }
  }

  /**
   * Handler when a square is dropped. Nullifies the initial target and resets hover state.
   * @param e
   */
  onDropHandler(e) {
    if (this.props.animateOnHover) {
      this.setState({
        isHover: false
      });
    }

    this.enterTarget = null;

    if (this.props.dropHandler !== undefined) {
      this.props.dropHandler(e);
    }
  }

  onMouseEnterHandler(e) {
    this.setState({ isPreview: true });
  }

  onMouseLeaveHandler(e) {
    this.setState({ isPreview: false });
  }

  onMouseMoveHandler(e) {
    this.setState({ previewX: e.clientX, previewY: e.clientY });
  }

  render() {
    const onDragFunc =
      this.props.dragHandler === undefined
        ? null
        : event => {
            this.props.dragHandler(event, this.props.occurrence);
          };

    const onDragOverFunc =
      this.props.dragEndHandler !== undefined || this.props.dragHandler !== undefined
        ? event => {
            event.preventDefault();
          }
        : null;

    const classes = classnames({
      square: true,
      hover: this.state.isHover
    });

    if (isNil(this.props.occurrence)) {
      return (
        <div
          className={classes}
          onDragOver={event => {
            event.preventDefault();
          }}
          onDrop={this.onDropHandler}
          onDragEnter={this.onDragEnterHandler}
          onDragLeave={this.onDragLeaveHandler}
        />
      );
    }

    const shrinkName = name => {
      const maxSize = 7;
      if (typeof name !== 'undefined' && name.length > maxSize) {
        return `${this.props.occurrence.name.substring(0, maxSize)}...`;
      }
      return name;
    };

    const occurrenceType = isArray(this.props.occurrence)
      ? 'stack'
      : this.props.occurrence.type
      ? this.props.occurrence.type
      : 'stack';

    const edition = this.props.editable ? (
      <span
        key="edit"
        className="square__picto square__picto--edit"
        onClick={this.props.editHandler}
      />
    ) : null;

    const deletion = this.props.deletable ? (
      <span
        key="delete"
        className="square__picto square__picto--delete"
        onClick={this.props.deleteHandler}
      />
    ) : null;

    let hoverImage = null;

    if (this.props.occurrence.type === 'stimuli' && this.props.occurrence.thumbnail_url) {
      const style = {
        left: `${this.state.previewX}px`,
        top: `${this.state.previewY}px`
      };

      const classes = classnames({
        stimuliHover: true,
        visible: this.state.isPreview
      });

      hoverImage = (
        <div className={classes} style={style}>
          <img src={this.props.occurrence.thumbnail_url} alt={this.props.occurrence.name} />
        </div>
      );
    }

    return (
      <div
        className={classes}
        data-type={occurrenceType}
        onDragOver={onDragOverFunc}
        onDragEnter={this.onDragEnterHandler}
        onDragLeave={this.onDragLeaveHandler}
        onDrop={this.onDropHandler}
        onMouseMove={this.onMouseMoveHandler}
        onMouseEnter={this.onMouseEnterHandler}
        onMouseLeave={this.onMouseLeaveHandler}
      >
        <div
          className="square__container"
          onClick={() => {
            isArray(this.props.occurrence) && this.props.onClickHandler();
          }}
          onDragStart={onDragFunc}
          onDragEnd={event => this.props.dragEndHandler(event)}
          draggable={this.props.draggable}
        >
          {edition}
          {deletion}
          {isArray(this.props.occurrence) ? (
            <p className="square_name">{this.props.occurrence.length}</p>
          ) : (
            <p className="square_name">{shrinkName(this.props.occurrence.name)}</p>
          )}
        </div>
        {hoverImage}
      </div>
    );
  }
}

Square.propTypes = {
  occurrence: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  draggable: PropTypes.bool,
  editable: PropTypes.bool,
  deletable: PropTypes.bool,
  animateOnHover: PropTypes.bool,
  dropHandler: PropTypes.func,
  dragHandler: PropTypes.func,
  dragEndHandler: PropTypes.func,
  onClickHandler: PropTypes.func,
  editHandler: PropTypes.func,
  deleteHandler: PropTypes.func
};

Square.defaultProps = {
  occurrence: null,
  draggable: false,
  editable: true,
  deletable: false,
  animateOnHover: false,
  dragEndHandler: () => {},
  onClickHandler: () => {},
  editHandler: () => {},
  deleteHandler: () => {}
};

export default Square;



// WEBPACK FOOTER //
// ./src/scenes/projects/scenes/singleProject/components/experimentalDesign/Square.js